import { findRowByGroupDeep, getNodePrimaryValue } from "./summaryUtils";
import { resolveReportKind } from "./reportKindResolver";

const MAX_SECTIONS = 12;
const MAX_ITEMS = 12;
const MAX_METRICS = 24;
const MAX_SERIES = 8;
const MAX_TOP_ITEMS = 12;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toText = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
};

const buildPeriod = (financialData, filters) => ({
  label:
    financialData?.columns?.[financialData.columns.length - 1]?.label ||
    financialData?.overview?.date ||
    filters?.period ||
    null,
  from: filters?.periodFrom || financialData?.overview?.date_from || null,
  to: filters?.periodTo || financialData?.overview?.date_to || financialData?.overview?.date || null,
  groupBy: filters?.groupBy || financialData?.overview?.group_by || null,
});

const buildBasePayload = (reportKind, reportType, financialData, filters) => ({
  reportKind,
  reportType,
  period: buildPeriod(financialData, filters),
  currency: financialData?.overview?.currency || filters?.currency || null,
  summary: {},
  sections: [],
  metrics: [],
  topItems: [],
  timeSeries: [],
  comparisons: [],
  notes: [],
});

const isHorizontalReport = (reportType = "") =>
  String(reportType || "").toLowerCase().includes("horizontal");

const buildDataAvailabilityNotes = (reportType, financialData) => {
  const notes = [];
  const columnCount = Array.isArray(financialData?.columns) ? financialData.columns.length : 0;

  if (isHorizontalReport(reportType) && columnCount < 2) {
    notes.push({
      type: "insufficient_periods",
      requiredPeriods: 2,
      availablePeriods: columnCount,
      message: "Horizontal analysis requires at least two periods; only one period is available.",
    });
  }

  return notes;
};

const normalizeItem = (node) => {
  const value = toNumber(getNodePrimaryValue(node));
  const label = toText(node?.label || node?.group || node?.id);

  if (!label || value === null || value === 0) return null;

  return { label, value };
};

const normalizeSection = (node) => {
  const total = toNumber(getNodePrimaryValue(node));
  const label = toText(node?.label || node?.group || node?.id);

  if (!label || total === null) return null;

  const items = (Array.isArray(node?.children) ? node.children : [])
    .map(normalizeItem)
    .filter(Boolean)
    .slice(0, MAX_ITEMS);

  return {
    key: toText(node?.group || node?.id || label)?.toLowerCase()?.replace(/\s+/g, "_") || null,
    label,
    total,
    items,
  };
};

const extractStatementSections = (rows = []) =>
  rows
    .flatMap((row) =>
      Array.isArray(row?.children) && row.children.length > 0
        ? row.children.map(normalizeSection)
        : [normalizeSection(row)]
    )
    .filter(Boolean)
    .slice(0, MAX_SECTIONS);

const buildTopItems = (sections = []) =>
  sections
    .flatMap((section) =>
      (section?.items || []).map((item) => ({
        label: item.label,
        value: item.value,
        section: section.label,
      }))
    )
    .sort((left, right) => Math.abs(right.value) - Math.abs(left.value))
    .slice(0, MAX_TOP_ITEMS);

const buildStatementSummary = (rows = []) => {
  const totals = rows
    .map((row) => normalizeItem(row))
    .filter(Boolean)
    .slice(0, MAX_SECTIONS);

  return totals.length > 0 ? { totals } : {};
};

const MAX_STATEMENT_SERIES = 16;

// A node's per-period values live in summary.sub_totals for classification/group rows
// (their `values` array is typically [0, 0, …]); leaf accounts carry them in `values`.
// Reading sub_totals first is what lets horizontal analysis see BOTH periods.
const nodePerColumnValues = (node) => {
  const subTotals = node?.summary?.sub_totals;
  if (Array.isArray(subTotals) && subTotals.some((value) => toNumber(value) !== null && toNumber(value) !== 0)) {
    return subTotals;
  }
  return Array.isArray(node?.values) ? node.values : [];
};

const collectSeriesNodes = (nodes, depth, maxDepth, bucket) => {
  for (const node of nodes) {
    if (bucket.length >= MAX_STATEMENT_SERIES) return;
    const label = toText(node?.label || node?.group || node?.id);
    const perColumn = nodePerColumnValues(node);
    if (label && perColumn.length >= 2) bucket.push({ label, perColumn });
    if (depth < maxDepth && Array.isArray(node?.children) && node.children.length > 0) {
      collectSeriesNodes(node.children, depth + 1, maxDepth, bucket);
    }
  }
};

const buildTimeSeries = (financialData) => {
  const columns = Array.isArray(financialData?.columns) ? financialData.columns : [];
  const rows = Array.isArray(financialData?.rows) ? financialData.rows : [];

  if (columns.length < 2 || rows.length === 0) return [];

  const seriesNodes = [];
  collectSeriesNodes(rows, 0, 1, seriesNodes); // top classifications + their direct sub-groups

  return seriesNodes
    .map(({ label, perColumn }) => {
      const points = columns
        .map((column, index) => ({
          periodId: column?.id || null,
          label: column?.label || null,
          date: column?.metadata?.date || null,
          value: toNumber(perColumn[index]),
        }))
        .filter((point) => point.value !== null);

      if (points.length < 2 || !points.some((point) => point.value !== 0)) return null;

      return { label, points };
    })
    .filter(Boolean)
    .slice(0, MAX_STATEMENT_SERIES);
};

const buildComparisonsFromTimeSeries = (timeSeries = []) =>
  timeSeries
    .map((series) => {
      const points = series?.points || [];
      if (points.length < 2) return null;

      const currentPoint = points[points.length - 1];
      const previousPoint = points[points.length - 2];
      if (currentPoint?.value === null || previousPoint?.value === null) return null;

      return {
        label: series.label,
        currentValue: currentPoint.value,
        previousValue: previousPoint.value,
      };
    })
    .filter(Boolean)
    .slice(0, MAX_STATEMENT_SERIES);

const flattenNumericMetrics = (value, prefix = "", bucket = []) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return bucket;

  Object.entries(value).forEach(([key, entryValue]) => {
    const metricLabel = prefix ? `${prefix}.${key}` : key;
    const numericValue = toNumber(entryValue);

    if (numericValue !== null) {
      bucket.push({ label: metricLabel, value: numericValue });
      return;
    }

    if (entryValue && typeof entryValue === "object" && !Array.isArray(entryValue)) {
      flattenNumericMetrics(entryValue, metricLabel, bucket);
    }
  });

  return bucket;
};

const buildCustomSections = (financialData = {}) =>
  Object.entries(financialData)
    .filter(([, value]) => value && typeof value === "object" && !Array.isArray(value))
    .map(([key, value]) => ({
      key,
      label: key,
      total: null,
      items: flattenNumericMetrics(value).slice(0, MAX_ITEMS),
    }))
    .filter((section) => section.items.length > 0)
    .slice(0, MAX_SECTIONS);

// Extraction is shape-driven, not report-name-driven: every real figure in Wafeq
// responses lives inside rows[]/children[]/summary.sub_totals[] arrays, which the
// generic flattenNumericMetrics path cannot reach — classifying tree data as
// "ratios" by name produced an almost numberless AI payload for ~17 report types.
const STATEMENT_SOURCES = ["balanceSheet", "profitAndLoss", "cashFlow"];

const BALANCE_KEY_GROUPS = {
  totalAssets: "ASSET",
  totalLiabilities: "LIABILITY",
  totalEquity: "EQUITY",
  currentAssets: "CURRENT_ASSET",
  currentLiabilities: "CURRENT_LIABILITY",
};

const buildBalanceKeyTotals = (rows = []) => {
  const totals = {};
  Object.entries(BALANCE_KEY_GROUPS).forEach(([label, groupName]) => {
    const row = findRowByGroupDeep(rows, groupName);
    if (row) {
      const value = toNumber(getNodePrimaryValue(row));
      if (value !== null) totals[label] = value;
    }
  });
  return Object.keys(totals).length > 0 ? totals : null;
};

const normalizeStatementTree = (financialData) => {
  const rows = Array.isArray(financialData?.rows) ? financialData.rows : [];
  const sections = extractStatementSections(rows);
  const timeSeries = buildTimeSeries(financialData);
  const keyTotals = buildBalanceKeyTotals(rows);

  return {
    summary: { ...buildStatementSummary(rows), ...(keyTotals && { keyTotals }) },
    sections,
    topItems: buildTopItems(sections),
    timeSeries,
    comparisons: buildComparisonsFromTimeSeries(timeSeries),
  };
};

const collectStatementTrees = (financialData = {}) =>
  STATEMENT_SOURCES.map((sourceKey) => ({ sourceKey, tree: financialData?.[sourceKey] })).filter(
    ({ tree }) => Array.isArray(tree?.rows)
  );

const normalizeCompositeTrees = (statementTrees, financialData, filters) => {
  const sections = statementTrees
    .flatMap(({ sourceKey, tree }) =>
      extractStatementSections(tree.rows).map((section) => ({ ...section, source: sourceKey }))
    )
    .slice(0, MAX_SECTIONS);

  const summary = {};
  statementTrees.forEach(({ sourceKey, tree }) => {
    const statementSummary = buildStatementSummary(tree.rows);
    if (statementSummary.totals) summary[sourceKey] = statementSummary.totals;
  });
  const keyTotals = buildBalanceKeyTotals(financialData?.balanceSheet?.rows || []);
  if (keyTotals) summary.keyTotals = keyTotals;

  const multiPeriodTree = statementTrees
    .map(({ tree }) => tree)
    .find((tree) => Array.isArray(tree?.columns) && tree.columns.length > 1);
  const timeSeries = multiPeriodTree ? buildTimeSeries(multiPeriodTree) : [];
  const primaryTree = statementTrees[0].tree;

  return {
    period: buildPeriod(primaryTree, filters),
    currency: primaryTree?.overview?.currency || filters?.currency || null,
    summary,
    sections,
    topItems: buildTopItems(sections),
    timeSeries,
    comparisons: buildComparisonsFromTimeSeries(timeSeries),
  };
};

export const normalizeFinancialPayload = ({ filters = {}, financialData, reportType }) => {
  const reportKind = resolveReportKind(reportType, financialData);
  const payload = {
    ...buildBasePayload(reportKind, reportType, financialData, filters),
    notes: buildDataAvailabilityNotes(reportType, financialData),
  };

  if (reportKind === "liquidity") {
    const balanceRows = financialData?.balanceSheet?.rows || [];
    const cashFlowRows = financialData?.cashFlow?.rows || [];
    const readGroupTotal = (groupName) => {
      const row = findRowByGroupDeep(balanceRows, groupName);
      return row ? toNumber(getNodePrimaryValue(row)) : null;
    };

    const currentAssets = readGroupTotal("CURRENT_ASSET");
    const currentLiabilities = readGroupTotal("CURRENT_LIABILITY");
    const workingCapital =
      currentAssets !== null && currentLiabilities !== null ? currentAssets - currentLiabilities : null;

    const summaryEntries = {
      totalAssets: readGroupTotal("ASSET"),
      totalLiabilities: readGroupTotal("LIABILITY"),
      totalEquity: readGroupTotal("EQUITY"),
      currentAssets,
      currentLiabilities,
      workingCapital,
      currentRatio:
        currentAssets !== null && currentLiabilities
          ? Number((currentAssets / currentLiabilities).toFixed(4))
          : null,
    };
    const summary = Object.fromEntries(
      Object.entries(summaryEntries).filter(([, value]) => value !== null)
    );

    const sections = [
      normalizeSection(findRowByGroupDeep(balanceRows, "CURRENT_ASSET")),
      normalizeSection(findRowByGroupDeep(balanceRows, "CURRENT_LIABILITY")),
      ...extractStatementSections(cashFlowRows),
    ]
      .filter(Boolean)
      .slice(0, MAX_SECTIONS);

    return {
      ...payload,
      period: buildPeriod(financialData?.balanceSheet, filters),
      currency: financialData?.balanceSheet?.overview?.currency || filters?.currency || null,
      summary,
      sections,
      metrics: Object.entries(summary)
        .map(([label, value]) => ({ label, value }))
        .slice(0, MAX_METRICS),
      topItems: buildTopItems(sections),
    };
  }

  // Shape 1: a single raw Wafeq statement tree (rows[]) — regardless of the
  // nominal kind (ratios/aging/trend/statement), tree extraction is the only
  // path that reaches the figures.
  if (Array.isArray(financialData?.rows)) {
    return {
      ...payload,
      ...normalizeStatementTree(financialData),
    };
  }

  // Shape 2: composite { balanceSheet / profitAndLoss / cashFlow } raw trees,
  // as produced by the multi-source report handlers.
  const statementTrees = collectStatementTrees(financialData);
  if (statementTrees.length > 0) {
    return {
      ...payload,
      ...normalizeCompositeTrees(statementTrees, financialData, filters),
    };
  }

  // Shape 3: legacy flat payloads (plain numeric objects) — the only shape the
  // generic flattener can actually read.
  if (reportKind === "ratios") {
    return {
      ...payload,
      metrics: flattenNumericMetrics(financialData).slice(0, MAX_METRICS),
    };
  }

  const sections = buildCustomSections(financialData);
  return {
    ...payload,
    summary: buildStatementSummary(financialData?.rows || []),
    sections,
    metrics: flattenNumericMetrics(financialData).slice(0, MAX_METRICS),
    topItems: buildTopItems(sections),
  };
};
